// ─── 🤖 Analytics · le linguette di Duet (22/10/2026) ─────────────────────────
//
// Mockup «Pacchetto 3 · La porta per Duet» v4: *«1A 2A 3B»*, *«4A 5A 6A»*, *«7A 8A 9A»*.
//   · 📸 Fotografia — quello che Duet legge ogni mattina, calcolato alle 6 (2A), e «Rifalla adesso»;
//   · 💬 Conversazioni — i messaggi dei soci col bot, socio per socio (3B);
//   · ✋ Proposte di Duet — quello che Duet vuole scrivere ai soci: non parte finché non lo approva
//     l'amministrazione (4A, 6A). Le stesse arrivano a chi approva su Telegram, coi bottoni (5A, 7A).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ETICHETTE_GESTO_DEL_PONTE,
  ETICHETTE_RICERCA,
  ETICHETTE_STATO_DI_PROPOSTA,
  ETICHETTE_TIPO_DI_FASCIA,
  ETICHETTE_TIPO_DI_PROPOSTA,
  GIORNI_DELLA_SETTIMANA,
  type ConversazioniDelGiorno,
  type Fotografia as LaFotografia,
  type GestoDelPonte,
  type Proposta,
  type TipoDiRicerca,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { giornoBreve, oggi } from '../../lib/time.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { usaModifica } from '../impostazioni/comune.js';

const FUSO = 'Europe/Rome';
const ora = (iso: string) =>
  new Intl.DateTimeFormat('it-IT', { timeZone: FUSO, hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  );
const quando = (iso: string) =>
  `${giornoBreve(new Intl.DateTimeFormat('sv-SE', { timeZone: FUSO }).format(new Date(iso)))} ${ora(iso)}`;
const oreInParole = (n: number) => (n === 1 ? '1 ora' : `${String(n).replace('.', ',')} ore`);

/**
 * 👥 I soci adatti a una partita (mockup «I soci adatti a una partita», *«1A 2C 3A 4B 5A»*): quanti sono,
 * e i primi 10 in ordine, col nome intero e il perché. Zero è un'informazione anche lui.
 */
function SociAdatti({ p }: { p: LaFotografia['partite'][number] }): React.JSX.Element {
  const quanti = p.adattiTotali === 1 ? '1 socio adatto' : `${p.adattiTotali} soci adatti`;
  if (p.adattiTotali === 0) return <span className="analytics-chi"> · 👥 {quanti}</span>;
  return (
    <details className="analytics-adatti">
      <summary>👥 {quanti}</summary>
      <ol>
        {p.adatti.map((a) => (
          <li key={a.memberId}>
            {a.nome} <span className="analytics-chi">· {a.perche}</span>
          </li>
        ))}
      </ol>
      {p.adattiTotali > p.adatti.length && (
        <p className="analytics-chi">L’elenco tiene i primi {p.adatti.length}, nell’ordine giusto.</p>
      )}
    </details>
  );
}

// ── 📸 La fotografia ─────────────────────────────────────────────────────────

export function Fotografia({ giorno }: { giorno: string }): React.JSX.Element {
  const qc = useQueryClient();
  const chiave = ['duet-fotografia', giorno];
  const dati = useQuery({
    queryKey: chiave,
    queryFn: () => chiama<{ fotografia: LaFotografia | null }>('GET', `/analytics/fotografia?date=${giorno}`),
  });
  const rifai = useMutation({
    meta: { fatto: 'fotografia rifatta' },
    mutationFn: () => chiama<LaFotografia>('POST', '/analytics/fotografia'),
    onSuccess: (f) => qc.setQueryData(chiave, { fotografia: f }),
  });

  if (dati.isPending) return <Attesa cosa="la fotografia" />;
  if (dati.error) return <Guasto errore={dati.error} />;
  const f = dati.data.fotografia;
  const diOggi = giorno === oggi();

  return (
    <div className="analytics-dal-bot">
      <p className="tenue piccolo">
        Le risposte che Duet trova ogni mattina, calcolate dal gestionale con le stesse regole del bot. La
        Segreteria vede le stesse cose che legge Duet.
      </p>
      <div className="riga-bottoni">
        {f && <span className="chip-fatto">calcolata alle {ora(f.madeAt)}</span>}
        {diOggi && (
          <button type="button" className="bordato" disabled={rifai.isPending} onClick={() => rifai.mutate()}>
            {rifai.isPending ? 'Un attimo…' : 'Rifalla adesso'}
          </button>
        )}
      </div>
      <Guasto errore={rifai.error} />
      {!f ? (
        <p className="tenue">
          Di quel giorno non c’è una fotografia: si fanno dal giorno in cui Duet ha la sua porta.
        </p>
      ) : (
        <div className="analytics-blocchi">
          <div className="analytics-blocco">
            <h3>🕳️ I buchi dei prossimi 14 giorni</h3>
            <p>
              <b>{oreInParole(f.buchi.ore)}</b> libere, di cui <b>{oreInParole(f.buchi.oreVicine)}</b> nei
              primi 3 giorni e <b>{oreInParole(f.buchi.orePunta)}</b> di punta.
            </p>
            <ul>
              {f.buchi.finestre.slice(0, 12).map((b) => (
                <li key={`${b.date}${b.courtName}${b.from}`}>
                  {giornoBreve(b.date)} · {b.band} · {b.courtName} · {b.from}–{b.to}
                  {b.kind !== 'normale' && (
                    <span className="analytics-chi"> · {ETICHETTE_TIPO_DI_FASCIA[b.kind]}</span>
                  )}
                </li>
              ))}
            </ul>
            {f.buchi.finestre.length > 12 && (
              <p className="analytics-chi">e altri {f.buchi.finestre.length - 12} pezzi di campo liberi.</p>
            )}
            {f.buchi.quasiSempreVuote.length > 0 && (
              <p className="analytics-chi">
                Vuote quasi sempre nelle ultime 4 settimane:{' '}
                {f.buchi.quasiSempreVuote
                  .map((q) => `${GIORNI_DELLA_SETTIMANA[q.weekday - 1]} ${q.band}`)
                  .join(', ')}
                .
              </p>
            )}
          </div>
          <div className="analytics-blocco">
            <h3>🎾 Le partite quasi fatte</h3>
            {f.partite.length === 0 ? (
              <p className="tenue">Tutte le partite aperte dei prossimi 14 giorni sono piene.</p>
            ) : (
              <ul>
                {f.partite.map((p) => (
                  <li key={p.bookingId}>
                    {giornoBreve(p.date)} · {p.time} · {p.courtName}
                    {p.organizzatore && ` · la partita di ${p.organizzatore}`} ·{' '}
                    <b>{p.mancano === 1 ? 'manca 1' : `mancano ${p.mancano}`}</b>
                    {p.livello !== null && ` · livello ${p.livello}`}
                    <SociAdatti p={p} />
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="analytics-blocco">
            <h3>🎓 Le lezioni libere</h3>
            {f.lezioni.length === 0 ? (
              <p className="tenue">I maestri hanno le ore dei prossimi 14 giorni già prese.</p>
            ) : (
              <ul>
                {f.lezioni.slice(0, 10).map((l) => (
                  <li key={`${l.date}${l.coachName}`}>
                    {l.coachName} · {giornoBreve(l.date)} · {l.times.slice(0, 6).join(', ')}
                    {l.times.length > 6 && ` e altre ${l.times.length - 6}`}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="analytics-blocco">
            <h3>📉 Quello che si è perso ieri</h3>
            <ul>
              <li>
                {f.persoIeri.ricerche.reduce((n, r) => n + r.quante, 0)} ricerche senza risultato
                {f.persoIeri.ricerche.slice(0, 3).map((r) => (
                  <span key={`${r.kind}${r.forDate}`} className="analytics-chi">
                    {' '}
                    · {ETICHETTE_RICERCA[r.kind as TipoDiRicerca] ?? r.kind}
                    {r.forDate && ` per ${giornoBreve(r.forDate)}`}: {r.quante}
                  </span>
                ))}
              </li>
              <li>
                {f.persoIeri.rifiuti.reduce((n, r) => n + r.quanti, 0)} rifiuti al bot
                {f.persoIeri.rifiuti.slice(0, 3).map((r) => (
                  <span key={`${r.action}${r.reason}`} className="analytics-chi">
                    {' '}
                    · {ETICHETTE_GESTO_DEL_PONTE[r.action as GestoDelPonte] ?? r.action}
                    {r.reason && ` («${r.reason}»)`}: {r.quanti}
                  </span>
                ))}
              </li>
            </ul>
          </div>
          <div className="analytics-blocco">
            <h3>💬 Come rispondono i soci</h3>
            {f.risposte ? (
              <ul>
                <li>
                  Avvisi: {f.risposte.avvisiMandati} mandati, {f.risposte.avvisiToccati} toccati
                </li>
                <li>
                  Inviti: {f.risposte.invitiMandati} mandati, {f.risposte.invitiAccettati} accettati
                </li>
                <li>
                  Lista d’attesa: {f.risposte.inLista} in lista, {f.risposte.entrati} ora in campo
                </li>
              </ul>
            ) : (
              <p className="tenue">
                I totali di ieri arrivano dal bot nella notte: alle 6 non c’erano ancora.
              </p>
            )}
          </div>
          <div className="analytics-blocco">
            <h3>🤖 La salute del bot</h3>
            <ul>
              <li>
                {f.bot.totaliArrivati
                  ? 'I totali di ieri sono arrivati.'
                  : 'I totali di ieri non sono ancora arrivati.'}
              </li>
              <li>
                Conversazioni di ieri: {f.bot.messaggi} messaggi, {f.bot.soci} soci
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 💬 Le conversazioni (3B) ─────────────────────────────────────────────────

export function Conversazioni({ giorno }: { giorno: string }): React.JSX.Element {
  const dati = useQuery({
    queryKey: ['duet-conversazioni', giorno],
    queryFn: () => chiama<ConversazioniDelGiorno>('GET', `/analytics/conversazioni?date=${giorno}`),
  });
  if (dati.isPending) return <Attesa cosa="le conversazioni" />;
  if (dati.error) return <Guasto errore={dati.error} />;
  const { soci } = dati.data;
  return (
    <div className="analytics-dal-bot">
      <p className="tenue piccolo">
        I messaggi dei soci col bot, arrivati la notte dopo. Solo di chi ha accettato la privacy, e si tengono
        90 giorni. Duet li legge con il cognome puntato.
      </p>
      {soci.length === 0 ? (
        <p className="tenue">Per quel giorno non sono arrivate conversazioni.</p>
      ) : (
        soci.map((s) => (
          <div key={s.memberId} className="analytics-blocco analytics-filo">
            <h3>
              {s.nome} <span className="analytics-chi">· {s.messaggi.length} messaggi</span>
            </h3>
            {s.messaggi.map((m, i) => (
              <div key={i} className={`analytics-bolla ${m.saidBy}`}>
                <time>{ora(m.at)}</time> {m.text}
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

// ── ✋ Le proposte di Duet (4A 6A) ───────────────────────────────────────────

export function Proposte(): React.JSX.Element {
  const qc = useQueryClient();
  const puòDecidere = usaModifica('manage_users');
  const [tutte, setTutte] = useState(false);
  const dati = useQuery({
    queryKey: ['duet-proposte', tutte],
    queryFn: () => chiama<{ items: Proposta[] }>('GET', `/analytics/proposte${tutte ? '' : '?stato=attesa'}`),
  });
  if (dati.isPending) return <Attesa cosa="le proposte" />;
  if (dati.error) return <Guasto errore={dati.error} />;
  const items = dati.data.items;
  return (
    <div className="analytics-dal-bot">
      <p className="tenue piccolo">
        Quello che Duet vuole scrivere ai soci. Non parte niente finché non lo approvi: solo allora entra
        nella coda degli avvisi e il bot lo consegna. Le stesse proposte arrivano a chi approva su Telegram,
        con i bottoni; quello che si decide là si vede qui.
      </p>
      {!puòDecidere && <p className="tenue">Approva l’amministrazione: questo accesso può guardare.</p>}
      <div className="riga-bottoni">
        <button type="button" className={tutte ? 'bordato' : ''} onClick={() => setTutte(false)}>
          In attesa
        </button>
        <button type="button" className={tutte ? '' : 'bordato'} onClick={() => setTutte(true)}>
          Tutte
        </button>
      </div>
      {items.length === 0 ? (
        <p className="tenue">
          {tutte ? 'Duet non ha ancora proposto niente.' : 'Nessuna proposta in attesa.'}
        </p>
      ) : (
        items.map((p) => (
          <UnaProposta
            key={p.id}
            p={p}
            puòDecidere={puòDecidere}
            decisa={() => void qc.invalidateQueries({ queryKey: ['duet-proposte'] })}
          />
        ))
      )}
    </div>
  );
}

function UnaProposta({
  p,
  puòDecidere,
  decisa,
}: {
  p: Proposta;
  puòDecidere: boolean;
  decisa: () => void;
}): React.JSX.Element {
  const [cambia, setCambia] = useState(false);
  const [testo, setTesto] = useState(p.text);
  const [no, setNo] = useState<string | null>(null);
  const decidi = useMutation({
    meta: {
      fatto: (_d, v) =>
        (v as { azione: string }).azione === 'approva' ? 'proposta approvata' : 'proposta scartata',
    },
    mutationFn: (v: { azione: 'approva' | 'scarta'; text?: string }) =>
      chiama<{ outcome: 'fatto' | 'rifiutato'; reason: string | null }>(
        'POST',
        `/analytics/proposte/${p.id}/decision`,
        v,
      ),
    onSuccess: (e) => {
      setNo(e.outcome === 'rifiutato' ? e.reason : null);
      decisa();
    },
  });
  const inAttesa = p.status === 'attesa';
  return (
    <div className="analytics-blocco analytics-proposta">
      <div className="riga-bottoni">
        <strong>{p.nome}</strong>
        <span className="analytics-chi">{ETICHETTE_TIPO_DI_PROPOSTA[p.kind]}</span>
        <span className={`stato-proposta ${p.status}`}>{ETICHETTE_STATO_DI_PROPOSTA[p.status]}</span>
        <span className="analytics-chi">proposta {quando(p.createdAt)}</span>
      </div>
      {p.domanda && <p className="analytics-chi">Ha scritto: «{p.domanda}»</p>}
      {cambia ? (
        <textarea value={testo} rows={3} maxLength={1500} onChange={(e) => setTesto(e.target.value)} />
      ) : (
        <div className="analytics-bolla bot">{p.finalText ?? p.text}</div>
      )}
      <p className="analytics-chi">Perché: {p.reason}</p>
      {!inAttesa && p.decisoDa && (
        <p className="analytics-chi">
          {ETICHETTE_STATO_DI_PROPOSTA[p.status]}
          {p.decidedAt && ` ${quando(p.decidedAt)}`} · {p.decisoDa}
          {p.decidedVia === 'telegram' && ' da Telegram'}
          {p.finalText && p.finalText !== p.text && ' · col testo cambiato'}
        </p>
      )}
      {no && <p className="guasto">Non fatto: {no}</p>}
      <Guasto errore={decidi.error} />
      {inAttesa && puòDecidere && (
        <div className="riga-bottoni">
          <button
            type="button"
            disabled={decidi.isPending || (cambia && testo.trim() === '')}
            onClick={() =>
              decidi.mutate(cambia ? { azione: 'approva', text: testo.trim() } : { azione: 'approva' })
            }
          >
            {cambia ? 'Approva il mio testo' : 'Approva e manda'}
          </button>
          {!cambia && (
            <button type="button" className="bordato" onClick={() => setCambia(true)}>
              Cambia il testo
            </button>
          )}
          <button
            type="button"
            className="bordato"
            disabled={decidi.isPending}
            onClick={() => decidi.mutate({ azione: 'scarta' })}
          >
            Scarta
          </button>
        </div>
      )}
    </div>
  );
}
